import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Instagram, MapPin, Clock, Phone, Coffee, Sparkles, ArrowRight, Camera } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ReservationForm } from "@/components/ReservationForm";
import { CommunityGallery } from "@/components/CommunityGallery";
import {
  BAR_NAME, CITY, ADDRESS, HOURS, PHONE_DISPLAY, INSTAGRAM_URL, INSTAGRAM_HANDLE, IMAGES, MENU, waLink,
} from "@/lib/config";

const Section = ({ id, children, className = "" }) => (
  <section id={id} className={`px-5 sm:px-10 lg:px-16 py-20 sm:py-28 ${className}`}>{children}</section>
);

const Reveal = ({ children, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.6, delay, ease: "easeOut" }}
  >
    {children}
  </motion.div>
);

export default function HomePage() {
  return (
    <div className="relative z-[2]">
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden">
        <img src={IMAGES.hero} alt="Interno bar MI.RO" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/70 to-background/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-background/80 to-transparent" />
        <div className="relative z-10 px-5 sm:px-10 lg:px-16 max-w-4xl">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="font-mono text-xs tracking-[0.4em] text-primary mb-5">
            CAFFÈ · COCKTAIL BAR · {CITY.toUpperCase()}
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
            className="font-serif text-6xl sm:text-7xl lg:text-8xl leading-[0.9] mb-6">
            {BAR_NAME}<br />
            <span className="italic text-primary/90">gusto</span> partenopeo.
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }}
            className="text-lg text-muted-foreground max-w-xl mb-9">
            Dal caffè del mattino all'ultimo cocktail della notte. Nel cuore di Napoli,
            dove ogni {10} caffè te ne offriamo uno.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.3 }}
            className="flex flex-wrap gap-4">
            <a href="#prenota">
              <Button data-testid="hero-reserve-btn" className="rounded-full h-12 px-8 bg-primary hover:bg-amber-500 text-primary-foreground font-semibold text-base transition-colors duration-300">
                Prenota un tavolo <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </a>
            <Link to="/tessera">
              <Button data-testid="hero-card-btn" variant="outline" className="rounded-full h-12 px-8 border-white/20 bg-white/5 hover:bg-white/10 text-white font-semibold text-base backdrop-blur-sm">
                <Coffee className="w-4 h-4 mr-2" /> La mia tessera
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* CHI SIAMO */}
      <Section id="chi-siamo">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <Reveal>
            <div className="relative">
              <img src={IMAGES.about} alt="Ambiente MI.RO" className="rounded-2xl w-full h-[460px] object-cover ring-1 ring-white/10" />
              <div className="absolute -bottom-6 -right-4 sm:right-6 bg-primary text-primary-foreground rounded-2xl px-6 py-5 shadow-2xl">
                <p className="font-serif text-4xl leading-none">2015</p>
                <p className="font-mono text-[10px] tracking-widest mt-1">DAL CUORE DI NAPOLI</p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="font-mono text-xs tracking-[0.3em] text-primary mb-4">CHI SIAMO</p>
            <h2 className="font-serif text-4xl sm:text-5xl leading-tight mb-6">
              Un rito napoletano, <span className="italic text-primary">reinterpretato</span>.
            </h2>
            <p className="text-muted-foreground text-lg leading-relaxed mb-5">
              MI.RO nasce dall'incontro tra la tradizione del caffè napoletano e la cultura
              contemporanea del cocktail bar. Un luogo intimo e curato, dove la miscelazione
              incontra l'ospitalità partenopea.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              Selezioniamo caffè specialty, distillati d'autore e ingredienti freschi del territorio
              per offrirti un'esperienza autentica, dal mattino fino a notte fonda.
            </p>
          </Reveal>
        </div>
      </Section>

      {/* MENU */}
      <Section id="menu" className="bg-zinc-950/60">
        <Reveal>
          <div className="text-center mb-14">
            <p className="font-mono text-xs tracking-[0.3em] text-primary mb-3">LE NOSTRE SPECIALITÀ</p>
            <h2 className="font-serif text-4xl sm:text-5xl">Il Menu</h2>
          </div>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {MENU.map((item, i) => (
            <Reveal key={item.title} delay={i * 0.1}>
              <div data-testid={`menu-item-${i}`} className="group rounded-2xl overflow-hidden bg-zinc-900 ring-1 ring-zinc-800 transition-transform duration-300 hover:-translate-y-2">
                <div className="h-56 overflow-hidden">
                  <img src={item.img} alt={item.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                </div>
                <div className="p-6">
                  <div className="flex items-baseline justify-between mb-2">
                    <h3 className="font-serif text-2xl">{item.title}</h3>
                    <span className="font-mono text-primary">{item.price}</span>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* GALLERIA */}
      <Section id="galleria">
        <Reveal>
          <div className="flex items-end justify-between mb-10">
            <div>
              <p className="font-mono text-xs tracking-[0.3em] text-primary mb-3">ATMOSFERA</p>
              <h2 className="font-serif text-4xl sm:text-5xl">Galleria</h2>
            </div>
            <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" data-testid="gallery-instagram"
              className="hidden sm:flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors">
              <Instagram className="w-5 h-5" /> @{INSTAGRAM_HANDLE}
            </a>
          </div>
        </Reveal>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
          {IMAGES.gallery.map((src, i) => (
            <Reveal key={`${src}-${i}`} delay={(i % 3) * 0.08}>
              <div className={`overflow-hidden rounded-xl ring-1 ring-white/10 ${i % 5 === 0 ? "md:row-span-2" : ""}`}>
                <img src={src} alt={`MI.RO ${i + 1}`} className={`w-full object-cover transition-transform duration-500 hover:scale-105 ${i % 5 === 0 ? "h-full min-h-[240px] md:min-h-[400px]" : "h-48 sm:h-60"}`} />
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* FOTO COMMUNITY */}
      <Section id="foto" className="bg-zinc-950/60">
        <Reveal>
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Camera className="w-4 h-4 text-primary" />
                <p className="font-mono text-xs tracking-[0.3em] text-primary">FOTO DELLA COMMUNITY</p>
              </div>
              <h2 className="font-serif text-4xl sm:text-5xl">I vostri momenti</h2>
              <p className="text-muted-foreground mt-3 max-w-lg">
                Le foto più belle scattate da noi e dai nostri clienti. Vuoi comparire anche tu?
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link to="/foto">
                <Button data-testid="home-share-photo" className="rounded-full h-11 px-6 bg-primary hover:bg-amber-500 text-primary-foreground font-semibold transition-colors duration-300">
                  <Camera className="w-4 h-4 mr-2" /> Condividi la tua foto
                </Button>
              </Link>
              <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" data-testid="home-foto-instagram">
                <Button variant="outline" className="rounded-full h-11 px-6 border-white/20 bg-white/5 hover:bg-white/10 text-white font-semibold">
                  <Instagram className="w-4 h-4 mr-2" /> @{INSTAGRAM_HANDLE}
                </Button>
              </a>
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <CommunityGallery limit={6} />
        </Reveal>
        <div className="text-center mt-8">
          <Link to="/foto" data-testid="home-foto-all" className="text-sm text-primary hover:text-amber-400 transition-colors font-medium">
            Vedi tutte le foto →
          </Link>
        </div>
      </Section>

      {/* PRENOTA */}
      <Section id="prenota" className="relative overflow-hidden">
        <img src={IMAGES.reserve} alt="" className="absolute inset-0 w-full h-full object-cover opacity-15" />
        <div className="absolute inset-0 bg-background/80" />
        <div className="relative z-10 grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
          <Reveal>
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-4 h-4 text-primary" />
              <p className="font-mono text-xs tracking-[0.3em] text-primary">PRENOTAZIONI</p>
            </div>
            <h2 className="font-serif text-4xl sm:text-5xl leading-tight mb-6">Riserva il tuo tavolo da {BAR_NAME}</h2>
            <p className="text-muted-foreground text-lg mb-6">
              Compila il modulo: riceverai conferma diretta su WhatsApp in pochi istanti.
              Per gruppi numerosi o eventi privati, scrivici pure.
            </p>
            <a href={waLink(`Ciao ${BAR_NAME}! Vorrei informazioni per un evento privato.`)} target="_blank" rel="noopener noreferrer"
              data-testid="reserve-whatsapp-link" className="inline-flex items-center gap-2 text-primary hover:text-amber-400 transition-colors font-medium">
              <Phone className="w-4 h-4" /> Oppure scrivici direttamente su WhatsApp
            </a>
          </Reveal>
          <Reveal delay={0.1}>
            <ReservationForm />
          </Reveal>
        </div>
      </Section>

      {/* CONTATTI / FOOTER */}
      <footer id="contatti" className="border-t border-white/10 px-5 sm:px-10 lg:px-16 py-16 bg-zinc-950">
        <div className="grid md:grid-cols-4 gap-10 max-w-6xl mx-auto">
          <div className="md:col-span-1">
            <p className="font-serif text-3xl mb-2">{BAR_NAME}</p>
            <p className="text-sm text-muted-foreground">Caffè & Cocktail Bar<br />{CITY}</p>
            <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" data-testid="footer-instagram"
              className="inline-flex items-center gap-2 mt-4 text-muted-foreground hover:text-primary transition-colors">
              <Instagram className="w-5 h-5" /> @{INSTAGRAM_HANDLE}
            </a>
          </div>
          <div>
            <h4 className="font-mono text-xs tracking-widest text-primary mb-4">DOVE SIAMO</h4>
            <p className="flex items-start gap-2 text-sm text-muted-foreground"><MapPin className="w-4 h-4 mt-0.5 text-primary shrink-0" /> {ADDRESS}</p>
            <p className="flex items-center gap-2 text-sm text-muted-foreground mt-3"><Phone className="w-4 h-4 text-primary" /> {PHONE_DISPLAY}</p>
          </div>
          <div>
            <h4 className="font-mono text-xs tracking-widest text-primary mb-4">ORARI</h4>
            {HOURS.map((r) => (
              <div key={r.d} className="flex items-center justify-between text-sm text-muted-foreground mb-2">
                <span className="flex items-center gap-2"><Clock className="w-3.5 h-3.5 text-primary/70" /> {r.d}</span>
                <span className="text-white">{r.h}</span>
              </div>
            ))}
          </div>
          <div>
            <h4 className="font-mono text-xs tracking-widest text-primary mb-4">LINK</h4>
            <div className="flex flex-col gap-2 text-sm">
              <a href="#prenota" className="text-muted-foreground hover:text-white transition-colors">Prenota</a>
              <Link to="/foto" data-testid="footer-foto-link" className="text-muted-foreground hover:text-white transition-colors">Foto community</Link>
              <Link to="/installa" data-testid="footer-install-link" className="text-muted-foreground hover:text-white transition-colors">Installa l'app</Link>
              <Link to="/tessera" data-testid="footer-card-link" className="text-muted-foreground hover:text-white transition-colors">Tessera fedeltà</Link>
              <Link to="/gestore" data-testid="footer-owner-link" className="text-muted-foreground hover:text-white transition-colors">Area gestore</Link>
            </div>
          </div>
        </div>
        <p className="text-center text-xs text-muted-foreground/60 mt-12">© {new Date().getFullYear()} {BAR_NAME} · {CITY}. Tutti i diritti riservati.</p>
      </footer>
    </div>
  );
}
